'use client';

import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Copy, RefreshCw, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { samlDecoderContent } from '../../data/saml-decoder-content';

type SamlData = {
    raw: string;
    decoded: string;
    pretty: string;
    type: 'request' | 'response' | null;
    error?: string;
};

export default function SamlDecoder() {
    const { toast } = useToast();
    const [samlInput, setSamlInput] = useState('');
    const [samlData, setSamlData] = useState<SamlData>({
        raw: '',
        decoded: '',
        pretty: '',
        type: null,
    });
    const [isLoading, setIsLoading] = useState(false);

    const decodeSaml = useCallback((samlString: string): SamlData => {
        try {
            // Remove any whitespace and newlines
            const cleanSaml = samlString.trim();

            // Check if it's a SAML request or response
            let type: 'request' | 'response' | null = null;
            if (cleanSaml.includes('samlp:AuthnRequest')) {
                type = 'request';
            } else if (cleanSaml.includes('samlp:Response')) {
                type = 'response';
            } else {
                throw new Error('Not a valid SAML request or response');
            }

            // Check if it's base64 encoded
            let decoded: string;
            let pretty: string;

            try {
                // Try to decode as base64
                decoded = atob(cleanSaml);
                // If successful, format the XML
                pretty = formatXml(decoded);
            } catch (e) {
                // If not base64, try to format the XML directly
                try {
                    decoded = cleanSaml;
                    pretty = formatXml(cleanSaml);
                } catch (innerError) {
                    throw new Error('Failed to parse SAML data. Please check the format.');
                }
            }

            return {
                raw: cleanSaml,
                decoded,
                pretty,
                type,
            };
        } catch (error) {
            console.error('Error decoding SAML:', error);
            return {
                raw: samlString,
                decoded: '',
                pretty: '',
                type: null,
                error: error instanceof Error ? error.message : 'Failed to decode SAML data',
            };
        }
    }, []);

    // Simple XML formatter
    const formatXml = (xml: string): string => {
        try {
            // Remove any existing formatting
            const xmlString = xml
                .replace(/\s+/g, ' ')
                .replace(/>\s+</g, '><')
                .trim();

            // Add new lines and indentation
            let formatted = '';
            let indent = '';
            const tab = '  ';
            let i = 0;
            let inTag = false;
            let inAttribute = false;

            for (i = 0; i < xmlString.length; i++) {
                const char = xmlString[i];

                if (char === '<') {
                    if (xmlString[i + 1] === '/') {
                        // Closing tag
                        indent = indent.slice(tab.length);
                        formatted += '\n' + indent;
                    } else if (xmlString[i - 1] === '>') {
                        // New tag after content
                        formatted += '\n' + indent;
                    } else if (i > 0) {
                        // New tag after another tag
                        formatted += '\n' + indent;
                    }
                    inTag = true;
                    inAttribute = false;
                    formatted += char;
                } else if (char === '>') {
                    inTag = false;
                    inAttribute = false;
                    formatted += char;

                    // Check if this is a self-closing tag
                    if (xmlString[i - 1] === '/') {
                        // Do nothing, it's self-closing
                    } else if (xmlString[i + 1] !== '<' || xmlString[i + 2] === '/') {
                        // Check if there's content before the next tag
                        let hasContent = false;
                        let j = i + 1;
                        while (j < xmlString.length && xmlString[j] !== '<') {
                            if (xmlString[j].trim() !== '') {
                                hasContent = true;
                                break;
                            }
                            j++;
                        }

                        if (!hasContent && xmlString[j + 1] === '/') {
                            // No content, next tag is closing
                        } else {
                            // Has content or next tag is not closing
                            indent += tab;
                        }
                    } else {
                        // Next tag is an opening tag
                        indent += tab;
                    }
                } else if (char === '"') {
                    inAttribute = !inAttribute;
                    formatted += char;
                } else if (char === ' ' && !inAttribute && xmlString[i - 1] !== '>' && xmlString[i + 1] !== '<') {
                    formatted += '\n' + indent + '  ';
                } else {
                    formatted += char;
                }
            }

            return formatted;
        } catch (e) {
            console.error('Error formatting XML:', e);
            return xml; // Return original if formatting fails
        }
    };

    const handleDecode = useCallback(() => {
        if (!samlInput.trim()) {
            toast({
                title: 'Error',
                description: 'Please enter a SAML request or response',
                variant: 'destructive',
            });
            return;
        }

        setIsLoading(true);
        try {
            const result = decodeSaml(samlInput.trim());
            setSamlData(result);

            if (result.error) {
                toast({
                    title: 'Error',
                    description: result.error,
                    variant: 'destructive',
                });
            } else {
                toast({
                    title: 'Success',
                    description: `SAML ${result.type} decoded successfully`,
                });
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: 'An error occurred while decoding the SAML data.',
                variant: 'destructive',
            });
        } finally {
            setIsLoading(false);
        }
    }, [samlInput, decodeSaml, toast]);

    const copyToClipboard = useCallback((text: string) => {
        navigator.clipboard.writeText(text);
        toast({
            title: 'Copied!',
            description: 'Text has been copied to clipboard.',
        });
    }, [toast]);

    const clearAll = useCallback(() => {
        setSamlInput('');
        setSamlData({
            raw: '',
            decoded: '',
            pretty: '',
            type: null,
        });
    }, []);

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <ToolPageIntro title="SAML Decoder - Free Online SAML Response Decoder">
                Decode and analyze SAML responses instantly with our free online tool. Parse SAML assertions, extract attributes, and debug authentication issues.
            </ToolPageIntro>
                    </div>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>SAML Data</CardTitle>
                        <CardDescription>
                            Paste your SAML request or response to decode and inspect its contents
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="saml">SAML Request/Response</Label>
                                <div className="flex space-x-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={clearAll}
                                        disabled={!samlInput && !samlData.raw}
                                    >
                                        <RefreshCw className="mr-2 h-4 w-4" />
                                        Clear
                                    </Button>
                                </div>
                            </div>
                            <Textarea
                                id="saml"
                                placeholder="Paste your SAML request or response here (can be base64 encoded or raw XML)"
                                className="min-h-[200px] font-mono text-sm"
                                value={samlInput}
                                onChange={(e) => setSamlInput(e.target.value)}
                            />
                        </div>

                        <div className="flex justify-end">
                            <Button onClick={handleDecode} disabled={!samlInput.trim() || isLoading}>
                                {isLoading ? 'Decoding...' : 'Decode SAML'}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {samlData.raw && (
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Decoded SAML {samlData.type ? `(${samlData.type})` : ''}</CardTitle>
                                    <CardDescription>
                                        {samlData.error ? (
                                            <span className="text-destructive flex items-center">
                                                <AlertCircle className="h-4 w-4 mr-2" />
                                                {samlData.error}
                                            </span>
                                        ) : (
                                            'Inspect the contents of your SAML data'
                                        )}
                                    </CardDescription>
                                </div>
                                {!samlData.error && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => copyToClipboard(samlData.pretty || samlData.decoded || samlData.raw)}
                                    >
                                        <Copy className="mr-2 h-4 w-4" />
                                        Copy All
                                    </Button>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent>
                            <Tabs defaultValue="pretty" className="w-full">
                                <TabsList className="grid grid-cols-2 w-full max-w-xs">
                                    <TabsTrigger value="pretty">Formatted</TabsTrigger>
                                    <TabsTrigger value="raw">Raw</TabsTrigger>
                                </TabsList>

                                <TabsContent value="pretty" className="mt-4">
                                    {samlData.pretty ? (
                                        <div className="relative rounded-md bg-muted p-4">
                                            <pre className="overflow-x-auto text-sm">
                                                <code>{samlData.pretty}</code>
                                            </pre>
                                        </div>
                                    ) : (
                                        <div className="text-muted-foreground text-sm p-4 bg-muted rounded-md">
                                            No formatted content available
                                        </div>
                                    )}
                                </TabsContent>

                                <TabsContent value="raw" className="mt-4">
                                    <div className="relative rounded-md bg-muted p-4">
                                        <pre className="overflow-x-auto break-all text-sm">
                                            <code>{samlData.decoded || samlData.raw}</code>
                                        </pre>
                                    </div>
                                </TabsContent>
                            </Tabs>
                        </CardContent>
                    </Card>
                )}

                {/* Informational Content */}
                <div className="mt-12">
                    <InformationalContent
                        title={samlDecoderContent.title}
                        description={samlDecoderContent.description}
                        content={samlDecoderContent.content}
                        faqs={samlDecoderContent.faqs}
                        relatedTools={samlDecoderContent.relatedTools}
                    />
                </div>
            </div>
        </div>
    );
}
